/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getPageZoomLevel, getPinchZoomCenterParameters } from "selectors/zoom";
import { getPageHandle } from "selectors/pageSelectors";
import { overlayEvents } from "overlays/OverlayEvents";
import { getPageBeforeOrAtOffset } from "selectors/pageNumber";
import { WheelPinchZoomEnabler } from "./WheelPinchZoomEnabler";
import { TouchPinchZoomEnabler } from "./TouchPinchZoomEnabler";
import { WebKitPinchZoomEnabler } from "./WebKitPinchZoomEnabler";
import { generateRandomString } from "utils/RandomStrings";
import { throttle } from "utils/RenderingThrottler";
import { isFirefox, isSafari } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { isMac, isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";
import { isUndefined } from "utils/lodashUtils";
import addEventListener from "dc-preview-utils/src/Utils/DomUtils";
import { ZOOM_TRIGGER_TYPE } from "constants/enums";

class PinchZoomManager {
  constructor() {
    this.resetState();
    this._throttledHandlePinchZoomInProgress = throttle(this._handlePinchZoomInProgress.bind(this));
    this.shouldThrottlePinchZoom = (isFirefox() && !isMac()) || isSafari() || isCurrentDeviceMobile();
  }

  handleWheelEvent(stores) {
    return evt => {
      /**
       * Not using isControlORCommandPressed from EventHandlingUtils because Chrome on mac sents wheel event
       * with ctrlKey as true.
       */
      if (evt.ctrlKey || evt.metaKey) {
        pinchZoomManager.handleWheel(evt, stores);
      }
    };
  }

  addEventListeners(stores) {
    this._removeWheelListener = addEventListener(window, "wheel", this.handleWheelEvent(stores), { passive: false });
  }

  removeEventListeners() {
    this._removeWheelListener && this._removeWheelListener();
  }

  resetState() {
    this._isPinchZoomInProgress = false;
    this._pinchZoomEnabler = null;
  }

  _reset(stores) {
    this.resetState();
    stores.zoom.setPinchZoomCenterParameters({
      centerX: 0,
      centerY: 0,
      centerPageIndex: 0,
      centerXRatio: 0,
      centerYRatio: 0
    });
  }

  _startPinchZoom(event, stores) {
    if (this._isPinchZoomInProgress === false) {
      if (event.type === "wheel") {
        this._pinchZoomEnabler = new WheelPinchZoomEnabler(event);
      } else if (event.type.startsWith("gesture")) {
        // for safari
        this._pinchZoomEnabler = new WebKitPinchZoomEnabler(event);
      } else {
        this._pinchZoomEnabler = new TouchPinchZoomEnabler();
      }
      if (this._pinchZoomEnabler && this._pinchZoomEnabler.shouldStartPinchZoom(event, stores)) {
        this._isPinchZoomInProgress = true;
        event.cancelable !== false && event.preventDefault();
        this._setUpPinchZoomCenter(event, stores);
      }
    }
  }

  _setUpPinchZoomCenter(event, stores) {
    const { centerY, centerX } = this._pinchZoomEnabler.getCenter(event);
    const { pageIndex } = getPageBeforeOrAtOffset(stores, centerY);
    const centerPageIndex = pageIndex;
    let pageRect = getPageHandle(stores, pageIndex).getBoundingClientRect();
    let w = pageRect.right - pageRect.left;
    let h = pageRect.bottom - pageRect.top;
    const centerXRatio = (centerX - pageRect.left) / w;
    const centerYRatio = (centerY - pageRect.top) / h;
    stores.zoom.setPinchZoomCenterParameters({
      centerX,
      centerY,
      centerPageIndex,
      centerXRatio,
      centerYRatio
    });

    const initialZoomLevel = getPageZoomLevel(stores, centerPageIndex);
    this._pinchZoomEnabler.setScaleFactor(event, initialZoomLevel);
    stores.zoom.setPinchZoomInitialZoom(initialZoomLevel);
  }

  _handlePinchZoomInProgress(event, stores) {
    if (
      this._isPinchZoomInProgress &&
      this._pinchZoomEnabler &&
      this._pinchZoomEnabler.isEventToBeConsumendByEnabler(event)
    ) {
      !event.defaultPrevented && event.cancelable !== false && event.preventDefault();
      const { centerPageIndex } = getPinchZoomCenterParameters(stores);
      let newZoomLevel = this._pinchZoomEnabler.getNewZoomLevel(event, getPageZoomLevel(stores, centerPageIndex));
      if (!Number.isNaN(newZoomLevel) && !isUndefined(newZoomLevel)) {
        stores.zoom.doPinchZoom(
          {
            newZoomLevel
          },
          true
        );
        overlayEvents.trigger("zoom", newZoomLevel, ZOOM_TRIGGER_TYPE.PINCH_ZOOM);
      }
    }
  }

  _endPinchZoom(event, stores) {
    if (this._isPinchZoomInProgress && this._pinchZoomEnabler.isEventToBeConsumendByEnabler(event)) {
      event.cancelable !== false && event.preventDefault();
      const { centerPageIndex } = getPinchZoomCenterParameters(stores);
      const newZoomLevel = getPageZoomLevel(stores, centerPageIndex);
      if (!Number.isNaN(newZoomLevel) && !isUndefined(newZoomLevel)) {
        stores.zoom.doPinchZoom(
          {
            newZoomLevel
          },
          false
        );
      }
      this._reset(stores);
    }
  }

  handleTouchStart(event, stores) {
    this._startPinchZoom(event, stores);
  }

  handleTouchEnd(event, stores) {
    this._endPinchZoom(event, stores);
  }

  handleTouchCancel(event, stores) {
    this._endPinchZoom(event, stores);
  }

  handleTouchMove(event, stores) {
    if (this.shouldThrottlePinchZoom) {
      this._isPinchZoomInProgress && event.cancelable !== false && event.preventDefault();
      this._throttledHandlePinchZoomInProgress(event, stores);
    } else {
      this._handlePinchZoomInProgress(event, stores);
    }
  }

  handleWheel(event, stores) {
    if (event.ctrlKey === true) {
      if (
        this._isPinchZoomInProgress === true &&
        this._pinchZoomEnabler &&
        this._pinchZoomEnabler.isEventToBeConsumendByEnabler &&
        this._pinchZoomEnabler.isEventToBeConsumendByEnabler(event) &&
        this._pinchZoomEnabler.didWheelEventOriginFromSamePoint(event)
      ) {
        this._handlePinchZoomInProgress(event, stores, "wheel");
        /* Since Wheel event doesnt provide an indication of last wheel event
                we wait for 100 ms to check if there has been a new wheel event. If there isnt
                one for 100 ms then we assume we need to end pinch zoom. Set a random key
                for every wheel event . if the key has not changed for 100 ms, that means
                user has left the control of the wheel and we can finally bring page
                images at that zoomlevel. Every time a wheel event is fired
                setTimeoutParams sets a new key and clears the previous timeout */
        const timeoutKey = generateRandomString();
        const timeoutCallback = window.setTimeout(() => {
          if (this._isPinchZoomInProgress) {
            if (this._pinchZoomEnabler && this._pinchZoomEnabler.getTimeoutKey() === timeoutKey) {
              this._endPinchZoom(event, stores);
            }
          }
        }, 100);
        this._pinchZoomEnabler.setTimeoutParams(timeoutKey, timeoutCallback);
      } else {
        /* reset in case user starts a new wheel
                zoom before the last one has ended */
        this._reset(stores);
        this._startPinchZoom(event, stores);
      }
    }
  }
}

export const pinchZoomManager = new PinchZoomManager();
